import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AccessTime,
  CheckCircle,
  DeleteOutline,
  DescriptionOutlined,
  DriveFileRenameOutline,
  EditOutlined,
  Error as ErrorIcon,
  FilterList,
  HistoryOutlined,
  MoreHoriz,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAtom } from "jotai";
import useDeepchat from "../../contexts/useDeepchat";
import { useRecentThreads } from "../../store/home/useRecentThreads";
import { useRecentDocumentStore } from "../../store/home/useRecentDocumentStore";
import { ChatResponse, TRecentDocumentsHistory } from "./models";
import { SidebarActivePanelAtom, SidebarDrawerExpandedAtom } from "./state/DeepchatState";
import { SIDEBAR_STATE, SidebarPanel } from "../../constants/sidebarEnums";
import { grey, indigo, red } from "../../colors";

/** Width of the collapsed icon rail */
export const ICON_RAIL_WIDTH = 56;
/** Width of the expanded nav drawer (icon + labels) */
export const NAV_DRAWER_WIDTH = 240;
/** Width of the content panel that slides out when a section is active */
const CONTENT_PANEL_WIDTH = 300;

/**
 * Compute the total sidebar pixel width based on current state.
 * Used by DeepchatPage to set the main content margin.
 */
export function getSidebarWidth(drawerExpanded: boolean, panelOpen: boolean): number {
  const navWidth = drawerExpanded ? NAV_DRAWER_WIDTH : ICON_RAIL_WIDTH;
  return panelOpen ? navWidth + CONTENT_PANEL_WIDTH : navWidth;
}

interface ChatSidebarProps {
  open: boolean;
  filterContent?: React.ReactNode;
  showDocuments?: boolean;
  onNewChat?: () => void;
}

/* ── Collapsed icon-only button ── */

function RailIcon({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip title={label} placement="right">
      <Box
        onClick={onClick}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 40,
          height: 40,
          borderRadius: "12px",
          cursor: "pointer",
          backgroundColor: active ? indigo[100] : "transparent",
          color: indigo[900],
          transition: "all 0.15s ease",
          "&:hover": { backgroundColor: active ? indigo[100] : grey[100] },
        }}
      >
        {icon}
      </Box>
    </Tooltip>
  );
}

/* ── Expanded icon + label row ── */

function NavRow({
  icon,
  label,
  active,
  onClick,
  component,
  to,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick?: () => void;
  component?: React.ElementType;
  to?: string;
}) {
  return (
    <ListItemButton
      onClick={onClick}
      {...(component ? { component, to } : {})}
      sx={{
        borderRadius: "8px",
        height: 40,
        minHeight: 40,
        maxHeight: 40,
        py: 0,
        px: 1.5,
        mb: 0.25,
        backgroundColor: active ? indigo[100] : "transparent",
        "&:hover": { backgroundColor: active ? indigo[100] : grey[100] },
      }}
    >
      <ListItemIcon sx={{ minWidth: 36, color: indigo[900] }}>{icon}</ListItemIcon>
      <ListItemText
        primary={label}
        primaryTypographyProps={{ fontSize: 14, fontWeight: 500, color: indigo[900], noWrap: true }}
      />
    </ListItemButton>
  );
}

/* ── Main Component ── */

export default function ChatSidebar({ open, filterContent, showDocuments, onNewChat }: ChatSidebarProps) {
  const [drawerExpanded, setDrawerExpanded] = useAtom(SidebarDrawerExpandedAtom);
  const [activePanel, setActivePanel] = useAtom(SidebarActivePanelAtom);
  const { historyLoading, history } = useRecentThreads();
  const { loading: docsLoading, docHistory } = useRecentDocumentStore();
  const location = useLocation();

  const handlePanelToggle = (panel: SidebarPanel) => {
    setActivePanel(activePanel === panel ? SIDEBAR_STATE.CLOSED : panel);
  };

  /** In expanded mode, clicking a label collapses the drawer and opens that panel */
  const handleExpandedItemClick = (panel: SidebarPanel) => {
    setDrawerExpanded(false);
    setActivePanel(panel);
  };

  const panelOpen = activePanel !== SIDEBAR_STATE.CLOSED && !drawerExpanded;
  const navWidth = drawerExpanded ? NAV_DRAWER_WIDTH : ICON_RAIL_WIDTH;
  const totalWidth = panelOpen ? ICON_RAIL_WIDTH + CONTENT_PANEL_WIDTH : navWidth;

  if (!open) return null;

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={open}
      sx={{
        "& .MuiDrawer-paper": {
          width: totalWidth,
          boxSizing: "border-box",
          borderRight: "1px solid",
          borderColor: grey[100],
          backgroundColor: "#FFFFFF",
          display: "flex",
          flexDirection: "row",
          position: "fixed",
          zIndex: 1200,
          transition: "width 225ms cubic-bezier(0.4, 0, 0.2, 1)",
          overflow: "hidden",
        },
      }}
    >
      {/* ── Navigation Column (icon rail or expanded nav) ── */}
      <Box
        sx={{
          width: navWidth,
          minWidth: navWidth,
          display: "flex",
          flexDirection: "column",
          alignItems: drawerExpanded ? "stretch" : "center",
          gap: 0.5,
          pt: 8.5,
          px: drawerExpanded ? 1 : 0,
          borderRight: panelOpen ? `1px solid ${grey[100]}` : "none",
          transition: "width 225ms cubic-bezier(0.4, 0, 0.2, 1), min-width 225ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {drawerExpanded ? (
          /* ── Expanded: icon + label rows ── */
          <>
            <NavRow
              icon={<EditOutlined sx={{ fontSize: 20 }} />}
              label="New chat"
              component={Link}
              to="/deepchat"
              onClick={() => {
                onNewChat?.();
                setActivePanel(SIDEBAR_STATE.CLOSED);
                setDrawerExpanded(false);
              }}
            />
            <NavRow
              icon={<HistoryOutlined sx={{ fontSize: 20 }} />}
              label="Recent threads"
              onClick={() => handleExpandedItemClick(SIDEBAR_STATE.RECENT_THREADS)}
            />
            {showDocuments && (
              <NavRow
                icon={<DescriptionOutlined sx={{ fontSize: 20 }} />}
                label="Recent documents"
                onClick={() => handleExpandedItemClick(SIDEBAR_STATE.DOCUMENTS)}
              />
            )}
            {filterContent && (
              <NavRow
                icon={<FilterList sx={{ fontSize: 20 }} />}
                label="Filters"
                onClick={() => handleExpandedItemClick(SIDEBAR_STATE.FILTERS)}
              />
            )}
          </>
        ) : (
          /* ── Collapsed: icon-only rail ── */
          <>
            <Tooltip title="New chat" placement="right">
              <Box
                component={Link}
                to="/deepchat"
                onClick={() => {
                  onNewChat?.();
                  setActivePanel(SIDEBAR_STATE.CLOSED);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: "12px",
                  color: indigo[900],
                  textDecoration: "none",
                  transition: "all 0.15s ease",
                  "&:hover": { backgroundColor: grey[100] },
                }}
              >
                <EditOutlined sx={{ fontSize: 22 }} />
              </Box>
            </Tooltip>

            <RailIcon
              icon={<HistoryOutlined sx={{ fontSize: 22 }} />}
              label="Recent threads"
              active={activePanel === SIDEBAR_STATE.RECENT_THREADS}
              onClick={() => handlePanelToggle(SIDEBAR_STATE.RECENT_THREADS)}
            />

            {showDocuments && (
              <RailIcon
                icon={<DescriptionOutlined sx={{ fontSize: 22 }} />}
                label="Recent documents"
                active={activePanel === SIDEBAR_STATE.DOCUMENTS}
                onClick={() => handlePanelToggle(SIDEBAR_STATE.DOCUMENTS)}
              />
            )}

            {filterContent && (
              <RailIcon
                icon={<FilterList sx={{ fontSize: 22 }} />}
                label="Filters"
                active={activePanel === SIDEBAR_STATE.FILTERS}
                onClick={() => handlePanelToggle(SIDEBAR_STATE.FILTERS)}
              />
            )}
          </>
        )}
      </Box>

      {/* ── Content Panel (only when collapsed rail + panel active) ── */}
      {panelOpen && (
        <Box
          sx={{
            width: CONTENT_PANEL_WIDTH,
            minWidth: CONTENT_PANEL_WIDTH,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            pt: 8,
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: indigo[900],
              px: 2,
              pb: 1,
            }}
          >
            {activePanel === SIDEBAR_STATE.RECENT_THREADS
              ? "Recent"
              : activePanel === SIDEBAR_STATE.DOCUMENTS
                ? "Documents"
                : "Filters"}
          </Typography>

          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {activePanel === SIDEBAR_STATE.RECENT_THREADS && (
              <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
                <RecentThreadsPanel historyLoading={historyLoading} history={history} currentPath={location.pathname} />
              </Box>
            )}
            {activePanel === SIDEBAR_STATE.DOCUMENTS && (
              <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
                <RecentDocumentsPanel loading={docsLoading} documents={docHistory} />
              </Box>
            )}
            {activePanel === SIDEBAR_STATE.FILTERS && filterContent && (
              <Box sx={{ px: 1.5, pb: 1, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                {filterContent}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Drawer>
  );
}

/* ── Recent Threads Sub-panel ── */

function RecentThreadsPanel({
  historyLoading,
  history,
  currentPath,
}: {
  historyLoading: boolean;
  history: ChatResponse[][];
  currentPath: string;
}) {
  return (
    <Box sx={{ px: 1 }}>
      {historyLoading ? (
        <Box sx={{ px: 1 }}>
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={36} sx={{ mb: 1 }} />
          ))}
        </Box>
      ) : (
        <List disablePadding>
          {history.map((thread, i) => {
            const item = (thread ?? []).at(0);
            if (!item) return null;

            return <RecentThreadRow key={String(item.user_chat_session) || i} item={item} currentPath={currentPath} />;
          })}
        </List>
      )}
    </Box>
  );
}

/* ── Single Recent Thread row with hover actions (rename / delete) ── */

function RecentThreadRow({ item, currentPath }: { item: ChatResponse; currentPath: string }) {
  const { renameThread, deleteThread } = useDeepchat();
  const navigate = useNavigate();
  const { id: activeThreadId } = useParams();

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [busy, setBusy] = useState(false);

  const sessionId = String(item.user_chat_session);
  const threadPath = `/deepchat/threads/${sessionId}`;
  const isActive = currentPath === threadPath;
  const threadTitle = item.subject || item.user_query || "Untitled thread";
  const menuOpen = Boolean(menuAnchor);

  const openMenu = (e: React.MouseEvent<HTMLElement>) => {
    // Prevent navigating to the thread when opening the action menu.
    e.preventDefault();
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  const closeMenu = () => setMenuAnchor(null);

  const startRename = () => {
    setRenameValue(threadTitle);
    setRenameOpen(true);
    closeMenu();
  };

  const startDelete = () => {
    setDeleteOpen(true);
    closeMenu();
  };

  const submitRename = () => {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === threadTitle) {
      setRenameOpen(false);
      return;
    }
    setBusy(true);
    renameThread(sessionId, trimmed)
      .then(() => setRenameOpen(false))
      .finally(() => setBusy(false));
  };

  const submitDelete = () => {
    setBusy(true);
    deleteThread(sessionId)
      .then(() => {
        setDeleteOpen(false);
        // If the deleted thread is the one currently open, return to a fresh chat.
        if (activeThreadId === sessionId) {
          navigate("/deepchat");
        }
      })
      .finally(() => setBusy(false));
  };

  return (
    <>
      <ListItemButton
        component={Link}
        to={threadPath}
        selected={isActive}
        title={threadTitle}
        sx={{
          borderRadius: "14px",
          py: 0.5,
          px: 1,
          mb: 0.25,
          "&.Mui-selected": {
            backgroundColor: "#DDEEF8",
            "&:hover": { backgroundColor: "#DDEEF8" },
          },
          "&:hover": { backgroundColor: grey[100] },
          // Reveal the action button on row hover or while its menu is open.
          "&:hover .thread-actions": { opacity: 1 },
          ".thread-actions": { opacity: menuOpen ? 1 : 0 },
        }}
      >
        <ListItemText
          primary={threadTitle}
          primaryTypographyProps={{
            fontSize: 13,
            fontWeight: isActive ? 800 : 400,
            color: indigo[900],
            noWrap: true,
          }}
        />
        <IconButton
          className="thread-actions"
          size="small"
          aria-label="Thread actions"
          onClick={openMenu}
          sx={{
            ml: 0.5,
            p: 0.25,
            color: indigo[900],
            transition: "opacity 0.15s ease",
            "&:hover": { backgroundColor: grey[200] },
          }}
        >
          <MoreHoriz sx={{ fontSize: 18 }} />
        </IconButton>
      </ListItemButton>

      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={startRename} sx={{ fontSize: 13, color: indigo[900] }}>
          <ListItemIcon sx={{ minWidth: 30, color: indigo[900] }}>
            <DriveFileRenameOutline sx={{ fontSize: 18 }} />
          </ListItemIcon>
          Rename
        </MenuItem>
        <MenuItem onClick={startDelete} sx={{ fontSize: 13, color: red[500] }}>
          <ListItemIcon sx={{ minWidth: 30, color: red[500] }}>
            <DeleteOutline sx={{ fontSize: 18 }} />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      {/* Rename dialog */}
      <Dialog open={renameOpen} onClose={() => !busy && setRenameOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: indigo[900], fontSize: 18 }}>Rename thread</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            size="small"
            margin="dense"
            label="Thread name"
            value={renameValue}
            inputProps={{ maxLength: 200 }}
            disabled={busy}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitRename();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)} disabled={busy} sx={{ color: indigo[900] }}>
            Cancel
          </Button>
          <Button
            onClick={submitRename}
            disabled={busy || !renameValue.trim()}
            variant="contained"
            sx={{
              backgroundColor: indigo[700],
              color: "#FFFFFF",
              justifyContent: "center",
              "& .MuiButton-startIcon": { color: "#FFFFFF" },
              ":hover": { backgroundColor: indigo[800] },
              "&.Mui-disabled": { backgroundColor: grey[200], color: grey[500] },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteOpen} onClose={() => !busy && setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: indigo[900], fontSize: 18 }}>Delete thread</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: indigo[900] }}>
            Are you sure you want to delete “{threadTitle}”? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={busy} sx={{ color: indigo[900] }}>
            Cancel
          </Button>
          <Button
            onClick={submitDelete}
            disabled={busy}
            variant="contained"
            sx={{
              backgroundColor: red[500],
              color: "#FFFFFF",
              justifyContent: "center",
              "& .MuiButton-startIcon": { color: "#FFFFFF" },
              ":hover": { backgroundColor: red[600] },
              "&.Mui-disabled": { backgroundColor: grey[200], color: grey[500] },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/* ── Recent Documents Sub-panel ── */

const STATUS_COLORS: Record<string, string> = {
  emailed: "#008C15",
  processing: "#FFA500",
  failed: "#C8102E",
};

function StatusIcon({ status }: { status: string }) {
  const color = STATUS_COLORS[status] ?? grey[500];
  if (status === "emailed") return <CheckCircle sx={{ fontSize: 14, color }} />;
  if (status === "failed") return <ErrorIcon sx={{ fontSize: 14, color }} />;
  return <AccessTime sx={{ fontSize: 14, color }} />;
}

function RecentDocumentsPanel({ loading, documents }: { loading: boolean; documents: TRecentDocumentsHistory[] }) {
  return (
    <Box sx={{ px: 1 }}>
      {loading ? (
        <Box sx={{ px: 1 }}>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={36} sx={{ mb: 1 }} />
          ))}
        </Box>
      ) : (
        <List disablePadding>
          {documents.map((doc, i) => (
            <ListItemButton
              key={i}
              sx={{
                borderRadius: "8px",
                py: 0.75,
                px: 1.5,
                mb: 0.25,
                "&:hover": { backgroundColor: grey[100] },
              }}
            >
              <ListItemText
                primary={doc.original_document_name}
                secondary={
                  <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                    <StatusIcon status={doc.document_status} />
                    <Typography component="span" sx={{ fontSize: 11, color: grey[600], textTransform: "capitalize" }}>
                      {doc.document_status}
                    </Typography>
                    <Typography component="span" sx={{ fontSize: 11, color: grey[500], ml: "auto" }}>
                      {dayjs(doc.create_datetime).format("M/D/YYYY h:mm A")}
                    </Typography>
                  </Box>
                }
                primaryTypographyProps={{
                  fontSize: 13,
                  fontWeight: 400,
                  color: indigo[900],
                  noWrap: true,
                }}
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Box>
  );
}
